import { Injectable } from '@angular/core';
import { AccountModel } from './models/account.model';
import { UserAccountLinkModel } from './models/user-account-link.model';
import { UserModel } from './models/user.model';
import { Subject } from 'rxjs';

/**
 * The app service class is used to store global data for the app in use.
 */
@Injectable({
  providedIn: 'root'
})
export class AppService {
  // the user's authentication status
  isAuthenticated = false;

  // the user's default account
  account: AccountModel = new AccountModel();

  // the user's own data
  user: UserModel = new UserModel();

  // user account link model used for permissions
  userAccountLink: UserAccountLinkModel = new UserAccountLinkModel();

  // stores the active quick access session metadata (created_at, last_active_at, inactivity_timeout_hours, hard_max_hours)
  quickAccessSessionChanged: Subject<any> = new Subject<any>();
  private _quickAccessSession: any = null;
  get quickAccessSession(): any { return this._quickAccessSession; }
  set quickAccessSession(value: any) {
    this._quickAccessSession = value;
    this.quickAccessSessionChanged.next(value);
  }

  // todo remove this when all dependencies on it are removed
  activeSiteId = 0;

  // a global date range filter for reporting
  date: Date = new Date();
  filter_date_range: Date[] = [
    new Date(new Date().setDate(new Date().getDate() - 30)),
    new Date()
  ]; // this.date.getFullYear(), this.date.getMonth() + 1, 0

  // used to view stats for all sites or selected sites. empty array means all sites.
  chart_selected_sites: number[] = [];

  constructor() {}

  /**
   * Overarching frontend feature groups used for subscription-tier gating.
   *
   * These are deliberately coarser than RBAC permissions. RBAC (rbac.service.ts) controls
   * per-feature, per-action access that clients manage themselves. This subscription gate sits
   * above that and controls which broad feature areas a tier unlocks at all. Each key groups the
   * related RBAC features. For example INCIDENTS covers both account-level incident registers
   * (registers-incidents) and site-level incidents (sites-incidents).
   *
   * Keep these keys in mind, not the granular RBAC permissions, when gating a whole feature area.
   */
  /**
   * ---------------------------------------------------------------------------------------------
   * Plan feature gating map by area (built collaboratively, screen by screen).
   * ---------------------------------------------------------------------------------------------
   * This records which overarching FEATURE gates each route/menu item, so planCan() can be applied
   * consistently. It is documentation only; it is filled in as we review each screen together.
   *
   * SITE CONTEXT (when editing/viewing a site). All of the following are reached from inside a site
   * and are gated by planCan() using the mapped FEATURE:
   *   Top tabs:
   *     - Overview                          -> SITES
   *     - Geofence Map                      -> SITES
   *     - Settings                          -> SITES
   *   Right-hand menu:
   *     - Details                           -> SITES
   *     - Noticeboard                       -> SITES
   *     - Contractors (Sub-Safety Plans)    -> SAFETY_PLANS   (rolled up: all safety plans)
   *     - Managers                          -> SITES          (sites-managers)
   *     - Users Worked On                   -> SITES          (sites-users-worked-on, rolled up into SITES)
   *     - Main Safety Plan                  -> SAFETY_PLANS   (sites-safety-plans)
   *     - Forms                             -> FORMS
   *     - QR Codes                          -> QR_CODES
   *     - Documents                         -> DOCUMENTS      (sites-documents)
   *     - Hazards & Risks                   -> HAZARDS
   *     - Hazardous Substances              -> HAZARDOUS_SUBSTANCES
   *     - Safety Observations               -> SAFETY_OBSERVATIONS
   *     - Incidents                         -> INCIDENTS
   *     - Toolbox Talks                     -> TOOLBOX_TALKS
   *     - Inspections & Audits              -> INSPECTIONS_AUDITS
   *     - Task Analysis Documents (Legacy)  -> TASK_ANALYSIS
   *     - TA/JSA/SWMS Register              -> TASK_ANALYSIS
   *     - Emergency Evacuation Plans        -> EMERGENCY_EVACUATION_PLAN
   *     - Notifications                     -> NOTIFICATION_ROLES
   *
   * CONTRACTOR CONTEXT (when editing/viewing a contractor). Reached from inside a contractor:
   *   Right-hand menu:
   *     - Details                           -> CONTRACTORS
   *     - Sites (Sub-Safety Plans)          -> SAFETY_PLANS   (contractors-safety-plans, rolled up)
   *     - Company Documents                 -> DOCUMENTS      (contractors-documents, rolled up)
   *     - Pre-Qualification                 -> PRE_QUALIFICATIONS
   *     - Subcontractors                    -> CONTRACTORS    (contractors-subcontractors)
   *     - Employees                         -> CONTRACTORS    (contractors-employees)
   *     - Related Accounts                  -> NETWORK        (networked-accounts)
   *
   * EMPLOYEE CONTEXT (when editing/viewing an employee). Reached from inside an employee:
   *   Right-hand menu:
   *     - Details                           -> EMPLOYEES
   *     - Training / Competencies           -> EMPLOYEES      (employees-training-competencies)
   *     - Manage Sites                      -> SITES          (employees-site-associations; requires Sites)
   *     - Assigned Forms                    -> FORMS
   *     - Notifications                     -> NOTIFICATION_ROLES
   *     - RBAC Roles                        -> RBAC_ROLES
   *     - Sites Visited                     -> SITE_ACCESS    (site visit/access reporting)
   *     - Sites Worked On                   -> SITES          (sites-users-worked-on, rolled up)
   *
   * LEFT NAVIGATION (top-level menu and its expandable groups):
   *   Top-level:
   *     - Dashboard                         -> (ungated; always available)
   *     - Sites                             -> SITES
   *     - Safety Plans                      -> SAFETY_PLANS
   *     - Contractors                       -> CONTRACTORS
   *     - Employees                         -> EMPLOYEES
   *       - Employees tab                   -> EMPLOYEES      (employees)
   *       - Access Requests tab             -> EMPLOYEES      (employees-access-requests)
   *     - Tasks                             -> TASKS
   *     - Account Inductions                -> INDUCTIONS
   *     - Messages                          -> MESSAGES
   *     - Client Sites                      -> CLIENT_SITES
   *     - Client Pre-Qualifications         -> CLIENT_PRE_QUALIFICATIONS
   *   Forms group:
   *     - Form Templates                    -> FORMS
   *     - Form Categories                   -> FORMS
   *     - Form Submissions                  -> FORMS
   *   Registers group:
   *     - Incidents                         -> INCIDENTS
   *     - Safety Observations               -> SAFETY_OBSERVATIONS
   *     - Hazards & Risks                   -> HAZARDS
   *     - Hazardous Substances              -> HAZARDOUS_SUBSTANCES
   *     - Inspections & Audits              -> INSPECTIONS_AUDITS
   *     - Toolbox Talks                     -> TOOLBOX_TALKS
   *     - Tools                             -> TOOLS
   *   Templates group:
   *     - Task Analysis Documents (Legacy)  -> TASK_ANALYSIS
   *     - TA/JSA/SWMS Templates             -> TASK_ANALYSIS
   *     - Documents                         -> DOCUMENTS
   *     - Contractor Pre-Qualifications     -> PRE_QUALIFICATIONS
   *     - Inspection/Audit Templates        -> INSPECTIONS_AUDITS
   *     - Inspection/Audit Questions        -> INSPECTIONS_AUDITS
   *     - Toolbox Talks Templates           -> TOOLBOX_TALKS
   *   Reports group:
   *     - Sign In/Out                       -> SITE_ACCESS
   *     - Currently Onsite                  -> SITE_ACCESS
   *     - Time Spent Onsite                 -> SITE_ACCESS
   *     - Visitors                          -> VISITORS
   *
   * SETTINGS DROPDOWN (gear icon, top-right):
   *     - Company Details                   -> ACCOUNT_SETTINGS
   *     - Settings                          -> ACCOUNT_SETTINGS
   *     - Company Documents                 -> COMPANY_DOCUMENTS (Free: account-company-documents)
   *     - RBAC Roles                        -> RBAC_ROLES
   *     - Notification Roles                -> NOTIFICATION_ROLES (Premium: mainly configures notifications for premium features)
   *     - Manage Tags                       -> TAGS            (Premium: only used alongside premium features)
   *   ACCOUNT_SETTINGS and RBAC_ROLES are always available (RBAC-gated, not plan-gated).
   *   NOTIFICATION_ROLES and TAGS require Premium.
   *
   * USER PROFILE DROPDOWN (person icon, top-right). Personal, user-scoped items gated by the core
   * user profile permission (RBAC), NOT by subscription plan (never plan-gated):
   *     - <User name> (details)             -> (ungated; core user profile)
   *     - Multi-Account Dashboard           -> (ungated; core user profile)
   *     - My Messages                       -> (ungated; core user profile)
   *     - My Tasks                          -> (ungated; core user profile)
   *     - My Forms                          -> (ungated; core user profile)
   *     - Training/Competency               -> (ungated; core user profile)
   *     - Change Login Credentials          -> (ungated; core user profile)
   *     - API Tokens                        -> (ungated; core user profile)
   *     - Log out                           -> (ungated)
   *
   * SITE-ONLY sub-features not shown in the left nav but covered under the site context above:
   *     - QR Codes                          -> QR_CODES
   *     - Emergency Evacuation Plans        -> EMERGENCY_EVACUATION_PLAN
   *     - Specified Systems                 -> SPECIFIED_SYSTEMS  (API-based feature; no UI)
   *
   * SUPER ADMIN MENU (system administrators only). Gated by is_sitesoft_admin /
   * isSystemsAdministrator(), NOT by subscription plan and NOT by client RBAC. Documented for
   * completeness; planCan() does not apply to these:
   *     - Admin, Accounts, All Sites, Users, QR Codes, Pre-Qualification Items,
   *       Development Schedule, Job Titles, Industries, PPE, Work Permits,
   *       TA/JSA/SWMS Document Templates, System Notifications, System Banners, Configuration
   *
   * All FEATURES below are now accounted for across the mapped areas.
   * ---------------------------------------------------------------------------------------------
   */
  public static readonly FEATURES = {
    // Free tier features.
    CONTRACTORS: 'contractors',                                 // contractors, contractors-subcontractors, contractors-employees
    EMPLOYEES: 'employees',                                     // employees, employees-training-competencies, employees-site-associations
    TASKS: 'tasks',                                             // tasks
    CLIENT_SITES: 'client-sites',                               // client-sites
    CLIENT_PRE_QUALIFICATIONS: 'client-pre-qualifications',     // client-pre-qualifications
    NETWORK: 'network',                                         // networked-users, networked-accounts (Free)

    // Account administration. Always available on every tier (RBAC-gated, not plan-gated).
    ACCOUNT_SETTINGS: 'account-settings',                       // account-settings (company details, configuration)
    RBAC_ROLES: 'rbac-roles',                                   // account-rbac-roles

    // Premium tier features.
    NOTIFICATION_ROLES: 'notification-roles',                   // account-notification-roles (Premium: mainly configures notifications for premium features)
    SITES: 'sites',                                             // sites, sites-managers, sites-users-worked-on
    SAFETY_PLANS: 'safety-plans',                               // safety-plans (SSSP), sites-safety-plans, contractors-safety-plans
    INDUCTIONS: 'inductions',                                   // account-inductions
    FORMS: 'forms',                                             // forms
    INCIDENTS: 'incidents',                                     // registers-incidents + sites-incidents
    SAFETY_OBSERVATIONS: 'safety-observations',                 // registers-safety-observations + sites-safety-observations
    HAZARDS: 'hazards',                                         // registers-hazards-risks + sites-hazards-risks
    HAZARDOUS_SUBSTANCES: 'hazardous-substances',               // registers-hazardous-substances + sites-hazardous-substances
    INSPECTIONS_AUDITS: 'inspections-audits',                   // registers-inspections-audits + sites-inspections-audits + templates-inspections-audits
    TOOLBOX_TALKS: 'toolbox-talks',                             // registers-toolbox-talks + sites-toolbox-talks + templates-toolbox-talks
    TOOLS: 'tools',                                             // registers-tools
    TASK_ANALYSIS: 'task-analysis',                             // sites-tajsaswms + sites-task-analysis-documents + templates-tajsaswms + templates-task-analysis-documents
    DOCUMENTS: 'documents',                                     // templates-documents + sites-documents + contractors-documents
    COMPANY_DOCUMENTS: 'company-documents',                     // account-company-documents (Free)
    PRE_QUALIFICATIONS: 'pre-qualifications',                   // contractors-pre-qualifications + templates-contractor-pre-qualifications
    SITE_ACCESS: 'site-access',                                 // reports-site-access (sign in/out, currently onsite, time spent onsite)
    QR_CODES: 'qr-codes',                                       // sites-qr-codes
    EMERGENCY_EVACUATION_PLAN: 'emergency-evacuation-plan',     // sites-emergency-evacuation-plan
    SPECIFIED_SYSTEMS: 'specified-systems',                     // sites-specified-systems
    MESSAGES: 'messages',                                       // messages
    VISITORS: 'visitors',                                       // reports-visitors
    TAGS: 'tags',                                               // account-tags
    ASSETS: 'assets',                                           // registers-assets + registers-asset-types + registers-vendors + sites-assets
  };

  /**
   * Sentinel plan feature meaning "never plan gated" (core personal permissions). Used by the
   * RBAC feature map for core-user-profile so those permissions are always available on any tier.
   */
  public static readonly ALWAYS_AVAILABLE = '__always_available__';

  /**
   * Authoritative map from RBAC feature slug (the value used in RBACService.RBAC_F and the segment
   * before the colon in a permission string, e.g. 'sites-incidents') to its owning plan feature.
   * Derived from the Mapping Reference table in the rbac-roles-plan-gating spec and MUST stay in
   * sync with the backend map in PlanFeatures::RBAC_PLAN_FEATURE_MAP (same keys, same values).
   * core-user-profile maps to the ALWAYS_AVAILABLE sentinel so it is never gated.
   */
  public static readonly RBAC_PLAN_FEATURE_MAP: { [rbacFeature: string]: string } = {
    // Account administration.
    'account-settings': AppService.FEATURES.ACCOUNT_SETTINGS,
    'account-rbac-roles': AppService.FEATURES.RBAC_ROLES,
    'account-company-documents': AppService.FEATURES.COMPANY_DOCUMENTS,
    'account-notification-roles': AppService.FEATURES.NOTIFICATION_ROLES,
    'account-tags': AppService.FEATURES.TAGS,
    'account-inductions': AppService.FEATURES.INDUCTIONS,

    // Contractors (Free area).
    'contractors': AppService.FEATURES.CONTRACTORS,
    'contractors-subcontractors': AppService.FEATURES.CONTRACTORS,
    'contractors-employees': AppService.FEATURES.CONTRACTORS,
    'contractors-safety-plans': AppService.FEATURES.SAFETY_PLANS,
    'contractors-documents': AppService.FEATURES.DOCUMENTS,
    'contractors-pre-qualifications': AppService.FEATURES.PRE_QUALIFICATIONS,

    // Employees (Free area; site associations roll up under premium Sites).
    'employees': AppService.FEATURES.EMPLOYEES,
    'employees-access-requests': AppService.FEATURES.EMPLOYEES,
    'employees-training-competencies': AppService.FEATURES.EMPLOYEES,
    'employees-site-associations': AppService.FEATURES.SITES,

    // Tasks and client-facing (Free areas).
    'tasks': AppService.FEATURES.TASKS,
    'client-sites': AppService.FEATURES.CLIENT_SITES,
    'client-pre-qualifications': AppService.FEATURES.CLIENT_PRE_QUALIFICATIONS,

    // Sites and site sub-features.
    'sites': AppService.FEATURES.SITES,
    'sites-managers': AppService.FEATURES.SITES,
    'sites-users-worked-on': AppService.FEATURES.SITES,
    'sites-safety-plans': AppService.FEATURES.SAFETY_PLANS,
    'sites-documents': AppService.FEATURES.DOCUMENTS,
    'sites-hazards-risks': AppService.FEATURES.HAZARDS,
    'sites-hazardous-substances': AppService.FEATURES.HAZARDOUS_SUBSTANCES,
    'sites-safety-observations': AppService.FEATURES.SAFETY_OBSERVATIONS,
    'sites-incidents': AppService.FEATURES.INCIDENTS,
    'sites-toolbox-talks': AppService.FEATURES.TOOLBOX_TALKS,
    'sites-inspections-audits': AppService.FEATURES.INSPECTIONS_AUDITS,
    'sites-task-analysis-documents': AppService.FEATURES.TASK_ANALYSIS,
    'sites-tajsaswms': AppService.FEATURES.TASK_ANALYSIS,
    'sites-qr-codes': AppService.FEATURES.QR_CODES,
    'sites-emergency-evacuation-plan': AppService.FEATURES.EMERGENCY_EVACUATION_PLAN,
    'sites-specified-systems': AppService.FEATURES.SPECIFIED_SYSTEMS,
    'sites-assets': AppService.FEATURES.ASSETS,

    // Safety plans and forms.
    'safety-plans': AppService.FEATURES.SAFETY_PLANS,
    'forms': AppService.FEATURES.FORMS,

    // Registers.
    'registers-incidents': AppService.FEATURES.INCIDENTS,
    'registers-safety-observations': AppService.FEATURES.SAFETY_OBSERVATIONS,
    'registers-hazards-risks': AppService.FEATURES.HAZARDS,
    'registers-hazardous-substances': AppService.FEATURES.HAZARDOUS_SUBSTANCES,
    'registers-inspections-audits': AppService.FEATURES.INSPECTIONS_AUDITS,
    'registers-toolbox-talks': AppService.FEATURES.TOOLBOX_TALKS,
    'registers-tools': AppService.FEATURES.TOOLS,
    'registers-assets': AppService.FEATURES.ASSETS,
    'registers-asset-types': AppService.FEATURES.ASSETS,
    'registers-vendors': AppService.FEATURES.ASSETS,

    // Templates.
    'templates-task-analysis-documents': AppService.FEATURES.TASK_ANALYSIS,
    'templates-tajsaswms': AppService.FEATURES.TASK_ANALYSIS,
    'templates-documents': AppService.FEATURES.DOCUMENTS,
    'templates-contractor-pre-qualifications': AppService.FEATURES.PRE_QUALIFICATIONS,
    'templates-inspections-audits': AppService.FEATURES.INSPECTIONS_AUDITS,
    'templates-toolbox-talks': AppService.FEATURES.TOOLBOX_TALKS,

    // Reports.
    'reports-site-access': AppService.FEATURES.SITE_ACCESS,
    'reports-visitors': AppService.FEATURES.VISITORS,

    // Messages and network.
    'messages': AppService.FEATURES.MESSAGES,
    'networked-users': AppService.FEATURES.NETWORK,
    'networked-accounts': AppService.FEATURES.NETWORK,

    // Core personal permission. Never plan gated.
    'core-user-profile': AppService.ALWAYS_AVAILABLE
  };

  /**
   * Feature areas available on the Free tier, plus account administration features that are
   * available on every tier (those are gated by RBAC, not by subscription). Everything not listed
   * here requires the Contractor or Premium tier.
   */
  public static readonly FREE_FEATURES: string[] = [
    // Free functional features.
    AppService.FEATURES.EMPLOYEES,
    AppService.FEATURES.CONTRACTORS,
    AppService.FEATURES.TASKS,
    AppService.FEATURES.CLIENT_SITES,
    AppService.FEATURES.CLIENT_PRE_QUALIFICATIONS,
    AppService.FEATURES.NETWORK,
    // Account administration (always available; RBAC-gated).
    AppService.FEATURES.ACCOUNT_SETTINGS,
    AppService.FEATURES.RBAC_ROLES,
    AppService.FEATURES.COMPANY_DOCUMENTS
  ];

  /**
   * Feature areas available on the Contractor tier: everything on Free plus Assets.
   *
   * Derived from FREE_FEATURES so the two tiers cannot drift apart. Mirrors
   * PlanFeatures::CONTRACTOR_FEATURES on the API, which is the authoritative gate.
   */
  public static readonly CONTRACTOR_FEATURES: string[] = [
    ...AppService.FREE_FEATURES,
    AppService.FEATURES.ASSETS
  ];

  /**
   * The subscription tiers the platform offers, in ascending order of access. Mirrors
   * PlanFeatures::SUBSCRIPTIONS on the API.
   */
  public static readonly SUBSCRIPTION_FREE: string = 'Free';
  public static readonly SUBSCRIPTION_CONTRACTOR: string = 'Contractor';
  public static readonly SUBSCRIPTION_PREMIUM: string = 'Premium';

  public static readonly SUBSCRIPTIONS: string[] = [
    AppService.SUBSCRIPTION_FREE,
    AppService.SUBSCRIPTION_CONTRACTOR,
    AppService.SUBSCRIPTION_PREMIUM
  ];

  /**
   * Whether a subscription tier unlocks a given feature area, ignoring who is asking.
   *
   * The single place the tier to feature relationship is expressed on the frontend, so the two
   * callers below cannot disagree. Premium unlocks everything, Contractor unlocks the free set plus
   * Assets, and Free (or any unknown or legacy tier) unlocks only the free set, so an unrecognised
   * value fails closed. Mirrors PlanFeatures::allows() on the API.
   *
   * @param subscription The account's subscription tier.
   * @param feature One of AppService.FEATURES.
   * @return True when that tier unlocks the feature.
   */
  static subscriptionUnlocks(subscription: string | undefined, feature: string): boolean {
    if (subscription === AppService.SUBSCRIPTION_PREMIUM) {
      return true;
    }

    if (subscription === AppService.SUBSCRIPTION_CONTRACTOR) {
      return AppService.CONTRACTOR_FEATURES.includes(feature);
    }

    return AppService.FREE_FEATURES.includes(feature);
  }

  /**
   * Is the given account the signed-in user's own personal account (SC-301)?
   *
   * A personal account exists so a user awaiting approval into a company still belongs somewhere, which is
   * what keeps them able to sign in and work. It stores the PERSON'S name rather than a generic label,
   * because that value lands on host site sign-in records and those have to identify who attended.
   *
   * That naming is why this test matters in several places. Presented verbatim, a personal account looks
   * like a person masquerading as a company, and offered as an employer it would let somebody employ
   * themselves. Callers use this to label it "My Account" or to leave it out of employer choices.
   *
   * Owned here rather than repeated per component: the same check is needed by the top navigation, the
   * account switcher, the employer selector and User Details, and four copies would drift.
   *
   * @param account Any account-shaped object carrying personal_user_id.
   * @return True when the account is this user's personal account.
   */
  isPersonalAccount(account: any): boolean {
    return !!(
      account &&
      account.personal_user_id &&
      Number(account.personal_user_id) === Number(this.user?.id)
    );
  }

  /**
   * Determine whether the active account's subscription tier unlocks a given overarching feature.
   *
   * This is a subscription-level gate only. Feature-specific access within an unlocked area is
   * still governed by RBAC (RBACService.userCanAccess()). Three tiers exist: Free, Contractor and
   * Premium, resolved by subscriptionUnlocks(). Super admins (is_sitesoft_admin) always have full
   * access, whatever the account is on.
   *
   * @param feature One of AppService.FEATURES.
   * @return True when the current subscription tier unlocks the feature.
   */
  planCan(feature: string): boolean {
    // Super admins always have full access, even on Free accounts.
    if ( this.user.is_sitesoft_admin ) {
      return true;
    }

    // A personal account unlocks no feature area at all. See accountFeaturesAvailable().
    if ( !this.accountFeaturesAvailable() ) {
      return false;
    }

    return AppService.subscriptionUnlocks(this.account.subscription, feature);
  }

  /**
   * Does the ACTIVE account offer account-level features at all?
   *
   * False on the user's own personal account, which is not a workspace. It exists so a user always
   * holds an approved membership somewhere, which is what keeps them able to sign in while a request to
   * join a company is pending (SC-301). It has no sites, no employees, no company content and exactly
   * one member, so every account feature there is either empty or meaningless: a Contractors register
   * with nobody in it, Company Details for a company that does not exist, RBAC Roles governing one
   * person who is already the administrator.
   *
   * What survives is the user-context set, which does not pass through here: the profile menu is gated
   * on the core-user-profile permission, which maps to ALWAYS_AVAILABLE and is never plan gated. The
   * dashboard's My Inductions widget likewise carries no plan or RBAC gate. Mobile and kiosk are
   * untouched, since the API exempts them from plan gating outright.
   *
   * Keyed on the personal account marker rather than the subscription tier, deliberately. A Free
   * BUSINESS account keeps everything it has today, so the tier cannot be the distinction. Mirrors
   * PlanFeatures::allowsForAccount() on the API, which is the authoritative gate.
   *
   * Super admins are NOT excluded here, because the two callers that need the bypass apply it
   * themselves before reaching this: planCan() checks is_sitesoft_admin first, and the navigation and
   * menu resolvers inherit that through planCan() and through RBACService.userCanAccess().
   *
   * @return True when the active account is a real account with account-level features.
   */
  accountFeaturesAvailable(): boolean {
    return !this.isPersonalAccount(this.account);
  }

  /**
   * Resolve the plan feature that governs an RBAC feature slug. Returns ALWAYS_AVAILABLE for core
   * personal permissions and undefined for an unmapped slug (callers treat unmapped as premium,
   * i.e. fail safe to hidden on Free).
   *
   * @param rbacFeature The RBAC feature slug (e.g. 'sites-incidents').
   * @return The owning plan feature, ALWAYS_AVAILABLE, or undefined when unmapped.
   */
  planFeatureForRbacFeature(rbacFeature: string): string | undefined {
    return AppService.RBAC_PLAN_FEATURE_MAP[rbacFeature];
  }

  /**
   * Whether the active account's tier unlocks the given RBAC feature. Always-available features
   * return true. Unmapped features fail safe to premium (hidden on Free; shown for Premium and
   * super admins). Super admin and Premium behaviour is inherited from planCan().
   *
   * @param rbacFeature The RBAC feature slug (e.g. 'sites-incidents').
   * @return True when the current tier unlocks the RBAC feature.
   */
  planCanRbacFeature(rbacFeature: string): boolean {
    const planFeature = this.planFeatureForRbacFeature(rbacFeature);
    if ( planFeature === AppService.ALWAYS_AVAILABLE ) {
      return true;
    }
    // Pass a non-free sentinel for the unmapped case so planCan() returns false on Free (fail safe
    // premium) and true for Premium / super admins.
    return this.planCan(planFeature ?? '__unmapped_premium__');
  }

  /**
   * Whether the active account's SUBSCRIPTION unlocks the given RBAC feature, ignoring who is asking.
   *
   * This is the account-scoped counterpart to planCanRbacFeature(). Use it to decide what an account's
   * RBAC role may CONTAIN, rather than what the current user may currently use. The difference is the
   * super admin bypass in planCan(): support staff editing a Free account were otherwise offered the
   * whole permission catalogue for that account's custom roles, while the account's own plan-derived
   * Administrators role correctly showed only the free subset.
   *
   * Mirrors PlanFeatures::subscriptionAllowsRbacFeature() on the API, which is the authoritative gate.
   *
   * @param rbacFeature The RBAC feature slug (e.g. 'sites-incidents').
   */
  subscriptionCanRbacFeature(rbacFeature: string): boolean {
    const planFeature = this.planFeatureForRbacFeature(rbacFeature);
    if ( planFeature === AppService.ALWAYS_AVAILABLE ) {
      return true;
    }
    // An unmapped slug is passed through as a non-free sentinel, so it stays hidden below Premium.
    return AppService.subscriptionUnlocks(
      this.account.subscription,
      planFeature ?? '__unmapped_premium__'
    );
  }

  /**
   * The subscription tiers offered by the platform, for the admin tier dropdowns and filters.
   */
  getSubscriptions(): string[] {
    return AppService.SUBSCRIPTIONS;
  }
}
